import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReceiptText, AlertTriangle, ArrowRight, CheckCircle2, BarChart3, Loader2, RefreshCw } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtMoney } from "../../lib/money";

/**
 * Trésorerie à venir™ — Dashboard cashflow forecast card (iter 56).
 *
 * Renders 5 compact buckets, a 90-day cumulative AreaChart, the next 3
 * upcoming invoices (with a one-click "Marquer payé"), and a tension-of-
 * treasury badge. Strictly read-only data, driven by the deterministic
 * `/api/dashboard/cashflow-forecast` endpoint.
 *
 * Self-hides when `has_data=false` (genuine "nothing due" — confirmed by a
 * successful fetch). A fetch *failure* is a distinct, separately-rendered
 * state (small inline card + retry) — it must never look the same as
 * "confirmed nothing due," the same trap `Dashboard.jsx` was hardened
 * against at the page level.
 */
const BUCKETS = [
  { key: "overdue", isDanger: true,  testId: "cashflow-bucket-overdue" },
  { key: "next_7",  isDanger: false, testId: "cashflow-bucket-7" },
  { key: "next_30", isDanger: false, testId: "cashflow-bucket-30" },
  { key: "next_60", isDanger: false, testId: "cashflow-bucket-60" },
  { key: "next_90", isDanger: false, testId: "cashflow-bucket-90" },
];

function tickFormatter(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;
}

export default function CashflowForecastCard({ testId = "dash-cashflow" }) {
  const { t, i18n } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchData = async () => {
    try {
      const r = await api.get("/dashboard/cashflow-forecast");
      setData(r.data);
      setError(false);
    } catch {
      // Keep any previously-loaded `data` (a failed refresh shouldn't wipe a
      // good prior read) — but never silently fall through to `!data` below,
      // which reads identically to "confirmed no invoices due."
      setError(true);
      if (data) {
        toast.error(
          t("cashflow.refresh_failed", { defaultValue: "Échec de l'actualisation de la trésorerie." }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Derive chart ticks (J+7, J+30, J+60, J+90) — only used if we have a
  // meaningful curve (≥ 2 non-zero amounts).
  const { chartReady, ticks, peakCumulative } = useMemo(() => {
    if (!data?.daily_series) return { chartReady: false, ticks: [], peakCumulative: 0 };
    const nonZero = data.daily_series.filter((d) => d.amount > 0).length;
    if (nonZero < 2) return { chartReady: false, ticks: [], peakCumulative: 0 };
    const series = data.daily_series;
    const pick = (i) => series[i]?.date;
    const tk = [pick(7), pick(30), pick(60), pick(90)].filter(Boolean);
    const peak = series.reduce((mx, d) => Math.max(mx, d.cumulative || 0), 0);
    return { chartReady: true, ticks: tk, peakCumulative: peak };
  }, [data]);

  if (loading) return null;

  // Fetch failed and we have nothing to show yet: this is NOT "confirmed no
  // invoices due" (that's the `!data.has_data` case below, only reachable on
  // a genuine successful response). Surface it distinctly instead of
  // vanishing — a workspace with real overdue invoices and a flaky request
  // deserves more than silence.
  if (error && !data) {
    return (
      <section
        className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 flex items-center gap-3"
        data-testid={`${testId}-error`}
      >
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 grid place-items-center shrink-0">
          <AlertTriangle size={16} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-qenva-navy text-[13.5px]">
            {t("cashflow.error_title", { defaultValue: "Trésorerie à venir™ indisponible" })}
          </p>
          <p className="text-zinc-500 text-[12px] mt-0.5">
            {t("cashflow.error_body", {
              defaultValue: "Impossible de vérifier vos échéances pour le moment — ceci ne signifie pas qu'il n'y en a pas.",
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          data-testid={`${testId}-error-retry`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold bg-zinc-950 text-white hover:bg-zinc-800 transition-colors"
        >
          <RefreshCw size={12} />
          {t("cashflow.retry", { defaultValue: "Réessayer" })}
        </button>
      </section>
    );
  }

  if (!data || !data.has_data) return null;

  const locale = (i18n.language || "fr").startsWith("fr") ? "fr-CA" : "en-CA";
  const dateFmt = new Intl.DateTimeFormat(locale, {
    year: "numeric", month: "short", day: "numeric",
  });

  const markPaid = async (docId) => {
    if (!docId) return;
    setMarkingId(docId);
    try {
      const r = await api.patch(
        `/documents/${docId}/invoice/payment-status`,
        { payment_status: "paid" },
      );
      toast.success(
        t("cashflow.toast_marked_paid", {
          defaultValue: `Facture marquée comme payée — ${r.data.resolved_alert_ids?.length || 0} alerte(s) résolue(s)`,
          count: r.data.resolved_alert_ids?.length || 0,
        }),
      );
      // Optimistic refetch
      await fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur inattendue";
      toast.error(`${t("cashflow.toast_mark_failed", { defaultValue: "Échec du marquage" })} — ${msg}`);
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-5"
      data-testid={testId}
    >
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <ReceiptText size={15} className="text-qenva-cat-invoice" />
          <h2 className="font-display font-bold text-qenva-navy text-[15px] tracking-tight truncate">
            {t("cashflow.title", { defaultValue: "Trésorerie à venir™" })}
          </h2>
          {data.tension_alert && (
            <span
              data-testid={`${testId}-tension-badge`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-qenva-cat-contract/10 border border-qenva-cat-contract/30 text-qenva-cat-contract text-[10.5px] font-bold uppercase tracking-wider"
            >
              <AlertTriangle size={10} strokeWidth={2.4} />
              {t("cashflow.tension_alert", { defaultValue: "Tension trésorerie" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate("/invoices/summary")}
            data-testid={`${testId}-see-summary`}
            className="text-qenva-blue text-[11.5px] font-semibold hover:underline inline-flex items-center gap-1"
          >
            <BarChart3 size={11} />
            {t("cashflow.see_summary", { defaultValue: "Voir le récapitulatif" })}
          </button>
          <span className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider">
            {data.currency}
          </span>
        </div>
      </header>

      {/* 5 buckets */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4" data-testid={`${testId}-buckets`}>
        {BUCKETS.map((b) => {
          const bucket = data.buckets[b.key] || { total: 0, count: 0 };
          const showDanger = b.isDanger && bucket.total > 0;
          return (
            <article
              key={b.key}
              data-testid={b.testId}
              className={`rounded-xl border p-3 ${
                showDanger
                  ? "border-qenva-cat-contract/30 bg-qenva-cat-contract/5"
                  : "border-zinc-200 bg-zinc-50/40"
              }`}
            >
              <p className={`text-[10px] uppercase tracking-wider font-bold ${
                showDanger ? "text-qenva-cat-contract" : "text-zinc-500"
              }`}>
                {t(`cashflow.bucket_${b.key}`, {
                  defaultValue: b.key === "overdue" ? "En retard"
                    : b.key === "next_7" ? "7 jours"
                    : b.key === "next_30" ? "30 jours"
                    : b.key === "next_60" ? "60 jours" : "90 jours",
                })}
              </p>
              <p className={`mt-1 text-[15px] font-display font-bold tabular-nums tracking-tight ${
                showDanger ? "text-qenva-cat-contract" : "text-qenva-navy"
              }`}>
                {fmtMoney(bucket.total, data.currency)}
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                {bucket.count === 0
                  ? t("cashflow.no_invoice", { defaultValue: "—" })
                  : t("cashflow.n_invoices", {
                      count: bucket.count,
                      defaultValue: `${bucket.count} facture${bucket.count > 1 ? "s" : ""}`,
                    })}
              </p>
            </article>
          );
        })}
      </div>

      {/* 90-day cumulative AreaChart — only if we have ≥ 2 non-zero days */}
      {chartReady && (
        <div className="mb-4 pt-3 border-t border-zinc-200/70" data-testid={`${testId}-chart`}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-1">
            {t("cashflow.chart_title", { defaultValue: "Cash out cumulé sur 90 jours" })}
          </p>
          {/* Explicit minWidth/minHeight + width=100% avoids the dev-only
              recharts width(-1)/height(-1) warning on first paint. */}
          <div style={{ width: "100%", minWidth: 200, height: 120, minHeight: 120 }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <AreaChart data={data.daily_series} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="cashflowFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A855F7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#A855F7" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  ticks={ticks}
                  tickFormatter={tickFormatter}
                  tick={{ fontSize: 10, fill: "#71717A" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => fmtMoney(v, data.currency)}
                  tick={{ fontSize: 10, fill: "#71717A" }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                />
                {data.tension_threshold > 0 && peakCumulative > data.tension_threshold && (
                  <ReferenceLine
                    y={data.tension_threshold}
                    stroke="#EF4444"
                    strokeDasharray="3 3"
                    label={{
                      value: t("cashflow.threshold", { defaultValue: "Seuil tension" }),
                      position: "right", fontSize: 9, fill: "#EF4444",
                    }}
                  />
                )}
                <Tooltip
                  contentStyle={{
                    background: "white", border: "1px solid #E4E4E7",
                    borderRadius: 8, fontSize: 11, padding: 8,
                  }}
                  formatter={(value, key) => [
                    fmtMoney(value, data.currency),
                    key === "cumulative"
                      ? t("cashflow.tooltip_cumulative", { defaultValue: "Cumul" })
                      : t("cashflow.tooltip_day_amount", { defaultValue: "Ce jour" }),
                  ]}
                  labelFormatter={(iso) => {
                    const d = new Date(iso);
                    return isNaN(d.getTime()) ? iso : dateFmt.format(d);
                  }}
                />
                <Area
                  type="monotone" dataKey="cumulative" stroke="#A855F7"
                  strokeWidth={1.8} fill="url(#cashflowFill)" dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top 3 upcoming */}
      {data.top_upcoming && data.top_upcoming.length > 0 && (
        <div className="pt-3 border-t border-zinc-200/70" data-testid={`${testId}-upcoming`}>
          <p className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
            {t("cashflow.upcoming_title", { defaultValue: "Prochaines échéances" })}
          </p>
          <ul className="space-y-1.5">
            {data.top_upcoming.map((inv, i) => {
              const due = new Date(inv.due_date);
              const dueLabel = isNaN(due.getTime()) ? inv.due_date : dateFmt.format(due);
              const isMarking = markingId === inv.doc_id;
              return (
                <li
                  key={inv.doc_id || `up-${i}`}
                  data-testid={`${testId}-upcoming-${i}`}
                  className="flex items-center justify-between gap-3 text-[12.5px] py-1 px-1 rounded-md hover:bg-zinc-50 transition-colors group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                      inv.days_until_due <= 7 ? "bg-qenva-cat-contract" : "bg-qenva-cat-invoice"
                    }`} />
                    <span className="text-qenva-navy font-semibold truncate" title={inv.vendor_name}>
                      {inv.vendor_name}
                    </span>
                    <span className="text-zinc-400 text-[11.5px] hidden sm:inline">
                      {t("cashflow.days_until_due", {
                        count: inv.days_until_due,
                        defaultValue: `dans ${inv.days_until_due} j`,
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-zinc-500 tabular-nums hidden sm:inline text-[11.5px]">{dueLabel}</span>
                    <span className="text-qenva-navy font-display font-bold tabular-nums">
                      {fmtMoney(inv.total_amount, data.currency)}
                    </span>
                    {inv.doc_id && (
                      <>
                        <button
                          type="button"
                          onClick={() => markPaid(inv.doc_id)}
                          disabled={isMarking}
                          data-testid={`${testId}-upcoming-${i}-mark-paid`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold text-qenva-cat-financing border border-qenva-cat-financing/30 hover:bg-qenva-cat-financing/10 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                          title={t("cashflow.mark_as_paid", { defaultValue: "Marquer comme payée" })}
                        >
                          {isMarking
                            ? <Loader2 size={11} className="animate-spin" />
                            : <CheckCircle2 size={11} />}
                          <span className="hidden sm:inline">
                            {t("cashflow.paid_short", { defaultValue: "Payée" })}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(`/analysis/${inv.doc_id}`)}
                          data-testid={`${testId}-upcoming-${i}-cta`}
                          className="text-qenva-blue opacity-0 group-hover:opacity-100 hover:underline transition-opacity inline-flex items-center"
                          aria-label={t("cashflow.open_invoice", { defaultValue: "Ouvrir la facture" })}
                        >
                          <ArrowRight size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
